/**
 * 📤 **그림·소리를 올릴 때의 한계와 그때 할 말** — 한 곳에서 정한다.
 *
 * 🔴 예전엔 상한이 엣지에만 있었고(`50MB`), 관리자는 실패를 **숫자로만** 받았다
 * (`upload 400`). 그러면 화면은 「저장 실패(엣지 확인)」이라고 말한다 —
 * 스캐너로 뜬 60MB PNG 를 올린 운영자는 **무엇이 문제인지 알 수가 없다.**
 * 가장 흔한 실수인데 가장 안 알려 주는 자리였다.
 */

/** 한 파일 최대 크기. 이보다 크면 엣지가 받지 않는다(R2 비용·DoS 방어). */
export const UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

/** 사람이 읽는 크기. 소수 한 자리까지 — 「52.4MB」 처럼. */
export function byteSay(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "?";
  if (bytes < 1024) return `${Math.round(bytes)}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

/** 엣지가 돌려준 올리기 실패 — 무엇을 하면 되는지까지 적는다. */
export function uploadFailSay(status: number, body?: { error?: unknown; bytes?: unknown }): string {
  const err = typeof body?.error === "string" ? body.error : "";
  if (err === "bad_size") {
    const n = typeof body?.bytes === "number" ? body.bytes : 0;
    return n === 0
      ? "파일이 비어 있어요. 다른 파일로 다시 올려 주세요."
      : `파일이 너무 커요(${byteSay(n)}). ${byteSay(UPLOAD_MAX_BYTES)} 까지 올릴 수 있어요 — 크기를 줄여서 올려 주세요.`;
  }
  if (err === "bad_format") {
    return "이 형식은 올릴 수 없어요. 그림은 WebP·PNG·JPG, 소리는 MP3 로 올려 주세요.";
  }
  if (err === "bad_content") {
    const fmt = typeof (body as { fmt?: unknown } | undefined)?.fmt === "string" ? (body as { fmt: string }).fmt.toUpperCase() : "";
    return `파일 이름은 ${fmt || "그 형식"} 인데 속은 다른 파일이에요. 확장자만 바꾼 파일이거나 깨진 파일일 수 있어요 — 원본을 그 형식으로 다시 저장해서 올려 주세요.`;
  }
  if (err === "rate_limited") return "잠깐 너무 많이 올렸어요. 1분 뒤에 다시 해 주세요.";
  if (err === "no_storage") return "파일 보관함(R2)이 연결되지 않았어요. 서버 설정을 확인해 주세요.";
  if (status === 401) return "401";
  return `upload ${status}`;
}

/**
 * 🔍 **파일 속이 이름표(형식)와 맞는가** — 앞머리(매직 바이트)로 본다 (2026-10-11).
 *
 * 엣지는 요청에 적힌 형식(content-type)만 믿고 저장했다. 확장자만 바꾼 파일·깨진 파일·글자 파일도
 * 그대로 받아, 아이 화면에서는 그림이 **조용히** 안 나왔다(로컬에 이상한 올리기를 넣어 보고). 실행되지는 않지만
 * (그림 형식으로만 내보낸다) 운영자는 「올렸는데 안 보인다」만 겪는다 — 올리는 순간 말해 준다.
 * 저장소의 진짜 파일(webp·png·jpg·mp4·mp3·glb·mind)로 맞춰 봤다.
 */
export function uploadContentMatches(fmt: string, head: Uint8Array): boolean {
  const at = (i: number, ...bytes: number[]) => bytes.every((b, k) => head[i + k] === b);
  const ascii = (i: number, s: string) => at(i, ...[...s].map((c) => c.charCodeAt(0)));
  switch (fmt) {
    case "png":
      return at(0, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a);
    case "jpg":
    case "jpeg":
      return at(0, 0xff, 0xd8, 0xff);
    case "webp":
      return ascii(0, "RIFF") && ascii(8, "WEBP");
    case "wav":
      return ascii(0, "RIFF") && ascii(8, "WAVE");
    case "mp4":
    case "m4a":
      return ascii(4, "ftyp");
    case "webm":
      return at(0, 0x1a, 0x45, 0xdf, 0xa3);
    case "ogg":
      return ascii(0, "OggS");
    case "mp3":
      // ID3 꼬리표로 시작하거나, 곧장 프레임 동기(FF Ex·FF Fx)로 시작한다
      return ascii(0, "ID3") || (head[0] === 0xff && ((head[1] ?? 0) & 0xe0) === 0xe0);
    case "glb":
      return ascii(0, "glTF");
    case "mind": {
      // MindAR 컴파일 결과는 msgpack 맵으로 시작한다(0x80~0x8f · 0xde · 0xdf)
      const b = head[0] ?? 0;
      return (b >= 0x80 && b <= 0x8f) || b === 0xde || b === 0xdf;
    }
    default:
      return false;
  }
}
